'use client'

import { NotificationData, notifications } from '@mantine/notifications'
import { Stack, Text } from '@mantine/core'
import { ActionError } from '../types'
import { ReactNode } from 'react'

const NOTIFICATION_POSITION = 'bottom-right'

type ShowNotificationProps = {
  type?: 'default' | 'loading' | 'success' | 'error'
  subtype?: 'requirements'
  title?: string
  message?: string | ReactNode
  requirements?: string[]
  error?: ActionError
} & Omit<NotificationData, 'title' | 'message'>

export function showNotification(payload: ShowNotificationProps): string {
  const notificationProps = parseNotificationProps(payload)
  return notifications.show(notificationProps)
}

type UpdateNotificationProps = {
  id: string
} & ShowNotificationProps

export function updateNotification({ id, ...payload }: UpdateNotificationProps): string {
  const notificationProps = parseNotificationProps(payload)
  return (
    notifications.update({
      id,
      ...notificationProps,
    }) || ''
  )
}

function parseNotificationProps({
  type,
  subtype,
  title,
  message,
  requirements = ['No requirements provided'],
  error,
  ...notificationData
}: ShowNotificationProps): NotificationData {
  if (subtype) {
    return CUSTOM_SUBTYPE[subtype]({
      type,
      title,
      requirements,
      ...notificationData,
    })
  }

  switch (type) {
    case 'loading':
      return {
        title: title || 'Loading',
        message,
        loading: true,
        autoClose: false,
        withCloseButton: false,
        position: NOTIFICATION_POSITION,
        ...notificationData,
      }
    case 'success':
      return {
        title: title || 'Success',
        message,
        loading: false,
        autoClose: 2000,
        withCloseButton: true,
        position: NOTIFICATION_POSITION,
        color: 'violet',
        ...notificationData,
      }
    case 'error':
      return {
        title: error?.status.toString() || title || 'Error',
        message: error?.message || message,
        loading: false,
        autoClose: 20000,
        withCloseButton: true,
        position: NOTIFICATION_POSITION,
        color: 'red',
        ...notificationData,
      }
    default:
      return {
        title: title || 'Under Construction',
        message: message || 'Thank you for your patience, we are working on getting this feature wired up.',
        loading: false,
        autoClose: 5000,
        withCloseButton: true,
        position: NOTIFICATION_POSITION,
        color: 'yellow',
        ...notificationData,
      }
  }
}

const CUSTOM_SUBTYPE = {
  requirements: ({ title, requirements = ['No requirements provided'], ...notificationData }: ShowNotificationProps) => {
    return parseNotificationProps({
      title: title || 'Requirements Missing',
      message: (
        <Stack gap={4} py={6} className="max-h-36 overflow-scroll">
          {requirements.map((r) => (
            <Text size="sm" key={r}>
              {' '}
              {r}{' '}
            </Text>
          ))}
        </Stack>
      ),
      autoClose: 20000,
      position: NOTIFICATION_POSITION,
      ...notificationData,
    })
  },
}
